import { Logger } from 'src/utils/Logger';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useSharePointSession } from 'src/data-capture/file-management/drop-zone/SharePointProvider';

type Props = {
  onPickedItems: (items: PickedItem[]) => void;
  onAccountChange: (account: { name?: string; username?: string } | null) => void;
  switchAccountRequested: boolean;
  onSwitchAccountHandled: () => void;
  /** File extensions to filter (e.g., ['.pdf', '.docx']). If not provided, all files are shown. */
  fileExtensions?: string[];
};

export const useSharepoint = ({
  onPickedItems,
  onAccountChange,
  switchAccountRequested,
  onSwitchAccountHandled,
  fileExtensions,
}: Props) => {
  const { instance, accounts } = useMsal();
  const { setPendingPicker, setSavedPath, setSavedFileExtensions } = useSharePointSession();
  const [isLoading, setIsLoading] = useState(false);
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const isAuthenticated = !!account;

  const [error, setError] = useState<string | null>();
  const [baseUrl, setBaseUrl] = useState('');
  const [isLoadingBaseUrl, setIsLoadingBaseUrl] = useState(false);

  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onAccountChange(
      account
        ? {
            name: account.name,
            username: account.username,
          }
        : null,
    );
  }, [account?.username, account?.name, onAccountChange]); // Checking individual fields to avoid infinite render loop

  const discoverBaseUrl = useCallback(async () => {
    if (!account) return;
    setIsLoadingBaseUrl(true);
    try {
      const tokenResp = await instance.acquireTokenSilent({
        scopes: ['Files.Read.All'],
        account,
      });
      const resp = await fetch('https://graph.microsoft.com/v1.0/me/drive', {
        headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const drive = await resp.json();
      const url = new URL(drive.webUrl);
      if (!url.hostname.endsWith('sharepoint.com')) {
        throw new Error(`Account is not a SharePoint work account (found: ${url.origin})`);
      }
      setBaseUrl(url.origin);
      Logger.info(`Discovered base URL: ${url.origin}`);
    } catch (err) {
      const errorMessage = `Base URL discovery failed: ${err instanceof Error ? err.message : String(err)}`;
      Logger.error(errorMessage, err);
      setError(
        'This feature requires a Microsoft work or school account with SharePoint. Personal Microsoft accounts are not supported.',
      );
    } finally {
      setIsLoadingBaseUrl(false);
    }
  }, [account?.homeAccountId, instance]);

  useEffect(() => {
    if (account && !baseUrl && !error) {
      discoverBaseUrl();
    }
  }, [account?.homeAccountId, baseUrl, discoverBaseUrl, error]);

  const getSharePointToken = async (resource: string): Promise<string> => {
    if (!account) throw new Error('No account signed in');
    const scopes = [`${resource.replace(/\/$/, '')}/.default`];
    try {
      const resp = await instance.acquireTokenSilent({ scopes, account });
      return resp.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const resp = await instance.acquireTokenPopup({ scopes, account });
        return resp.accessToken;
      }
      throw err;
    }
  };

  const getDownloadUrls = async (items: PickedItem[]): Promise<PickedItem[]> => {
    if (!account) return items;

    try {
      const tokenResp = await instance.acquireTokenSilent({
        scopes: ['Files.Read.All'],
        account,
      });

      const enrichedItems: PickedItem[] = [];

      for (const item of items) {
        const driveId = item.parentReference?.driveId;
        const itemId = item.id;

        if (!driveId || !itemId) {
          Logger.info(`Skipping item ${item.name || itemId}: missing driveId or itemId`);
          continue;
        }

        try {
          const response = await fetch(
            `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`,
            {
              headers: {
                Authorization: `Bearer ${tokenResp.accessToken}`,
              },
            },
          );

          if (!response.ok) {
            Logger.error(
              `Failed to get download URL for ${item.name}: ${response.statusText}`,
              response,
            );
            continue;
          }

          const data = await response.json();
          const downloadUrl = data['@microsoft.graph.downloadUrl'];
          const size = data.size;
          const createdDateTime: string | undefined =
            data.fileSystemInfo?.createdDateTime ?? data.createdDateTime;

          if (downloadUrl) {
            enrichedItems.push({
              ...item,
              downloadUrl,
              size,
              createdDateTime,
            });
            Logger.info(`Got download URL for ${item.name}: ${downloadUrl}`);
          }
        } catch (err) {
          Logger.error(
            `Error fetching download URL for ${item.name}: ${err instanceof Error ? err.message : String(err)}`,
            err,
          );
        }
      }

      Logger.info(`Got ${enrichedItems.length} download URL(s)`);
      return enrichedItems;
    } catch (err) {
      Logger.error(
        `Failed to get download URLs: ${err instanceof Error ? err.message : String(err)}`,
        err,
      );
      return items;
    }
  };

  const handleLoginPress = () => {
    setIsLoading(true);
    setPendingPicker(true);
    setSavedPath(window.location.pathname);
    if (fileExtensions && fileExtensions.length > 0) {
      setSavedFileExtensions(fileExtensions);
    }
    handleLogin();
    setIsLoading(false);
  };

  const handleLogin = (forceSelect = false) => {
    setError(null);
    instance.loginRedirect({
      scopes: ['User.Read', 'Files.Read.All'],
      prompt: forceSelect ? 'select_account' : undefined,
    });
  };

  useEffect(() => {
    if (switchAccountRequested) {
      handleLogin(true);
      onSwitchAccountHandled();
    }
  }, [switchAccountRequested]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  const handleOpenPicker = async () => {
    if (!account || !baseUrl) {
      setError('Could not open picker. Please reconnect your SharePoint account.');
      return;
    }

    setError(null);

    try {
      const channelId = String(Date.now());

      const pickerParams: Record<string, unknown> = {
        sdk: '8.0',
        entry: {
          oneDrive: { files: {} },
        },
        authentication: {},
        messaging: {
          origin: window.location.origin,
          channelId,
        },
        selection: {
          mode: 'multiple',
        },
        typesAndSources: {
          mode: 'files',
          pivots: {
            oneDrive: true,
            recent: true,
          },
          ...(fileExtensions &&
            fileExtensions.length > 0 && {
              filters: fileExtensions.map(ext => (ext.startsWith('.') ? ext : `.${ext}`)),
            }),
        },
      };

      const authToken = await getSharePointToken(baseUrl);
      Logger.info('Acquired SharePoint token');

      const win = window.open('', 'SharePointPicker', 'width=1080,height=680');
      if (!win) {
        setError('Popup was blocked. Please allow popups for this site.');
        return;
      }

      const queryString = new URLSearchParams({
        filePicker: JSON.stringify(pickerParams),
      });
      const pickerUrl = `${baseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;

      const form = win.document.createElement('form');
      form.setAttribute('action', pickerUrl);
      form.setAttribute('method', 'POST');
      win.document.body.append(form);

      const input = win.document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', 'access_token');
      input.setAttribute('value', authToken);
      form.appendChild(input);

      form.submit();
      Logger.info('Picker form submitted');

      const handleInit = (event: MessageEvent) => {
        if (
          event.source === win &&
          event.data?.type === 'initialize' &&
          event.data?.channelId === channelId
        ) {
          window.removeEventListener('message', handleInit);
          Logger.info('MessagePort established');

          const port = event.ports[0];

          port.addEventListener('message', async (msg: MessageEvent) => {
            const { data } = msg;

            if (data.type === 'notification') {
              Logger.info(`Notification: ${JSON.stringify(data.data)}`);
              return;
            }

            if (data.type !== 'command') return;

            port.postMessage({ type: 'acknowledge', id: data.id });

            const command = data.data;
            Logger.info(`Command: ${command.command}`);
            switch (command.command) {
              case 'authenticate': {
                try {
                  const token = await getSharePointToken(command.resource);
                  port.postMessage({
                    type: 'result',
                    id: data.id,
                    data: { result: 'token', token },
                  });
                  Logger.info(`Token provided for: ${command.resource}`);
                } catch (err) {
                  Logger.error(`Auth failed`, err);
                  port.postMessage({
                    type: 'result',
                    id: data.id,
                    data: {
                      result: 'error',
                      error: { code: 'authError', message: String(err) },
                    },
                  });
                }
                break;
              }
              case 'pick': {
                const items = command.items || [];
                port.postMessage({
                  type: 'result',
                  id: data.id,
                  data: { result: 'success' },
                });
                Logger.info(`Picked ${items.length} file(s)`);

                if (items.length > 0) {
                  const enrichedItems = await getDownloadUrls(items);
                  onPickedItems(enrichedItems);
                }

                win.close();
                break;
              }
              case 'close': {
                Logger.info('Picker closed');
                win.close();
                break;
              }
              default: {
                Logger.info(`Unhandled command: ${command.command}`);
                port.postMessage({
                  type: 'result',
                  id: data.id,
                  data: {
                    result: 'error',
                    error: { code: 'unsupportedCommand', message: command.command },
                    isExpected: true,
                  },
                });
                break;
              }
            }
          });

          port.start();
          port.postMessage({ type: 'activate' });
          Logger.info('Picker activated');
        }
      };

      window.addEventListener('message', handleInit);

      const checkClosed = setInterval(() => {
        if (win.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleInit);
          cleanupRef.current = null;
          Logger.info('Popup closed');
        }
      }, 1000);

      cleanupRef.current = () => {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleInit);
        if (!win.closed) {
          win.close();
        }
      };
    } catch (err) {
      setError(`Failed to open picker: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return {
    error,
    isAuthenticated,
    handleLoginPress,
    isLoading,
    handleOpenPicker,
    handleClick: isAuthenticated ? handleOpenPicker : handleLoginPress,
    baseUrl,
    isLoadingBaseUrl,
  };
};
